import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Trash2, Upload } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Controller, FormProvider, type Path, type Resolver, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { postApplication, signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
import type {
  IGetApplicationFormInfoServiceRes,
  IPostApplicationServicePayload,
  ISignApplicationPublicAssetPayload,
} from '@/api/Rover/ApplicationForm/types';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent } from '@/components/shadcn/card';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select';
import { Textarea } from '@/components/shadcn/textarea';
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';
import { cn } from '@/lib/utils';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import { FieldError } from './components/FieldError';
import { FormStepShell } from './components/FormStepShell';
import { StepNavigation } from './components/StepNavigation';
import {
  acquisitionTypeOptions,
  applicantIdentityOptions,
  constructionTypeOptions,
  initialGmfDeveloperPipelineValues,
  organizationTypeOptions,
  professionalTrainingOptions,
  projectByRightOptions,
  siteControlStatusOptions,
  yesNoOptions,
} from './gmfDeveloperPipeline.constants';
import { gmfDeveloperPipelineSchema, gmfStepSchemas } from './gmfDeveloperPipeline.schema';
import type { GmfCompletedProject, GmfDeveloperPipelineFormValues, GmfProjectFile } from './types/gmfDeveloperPipeline.types';

interface GMFDeveloperPipelineApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const stepTitles = ['Project Details', 'Applicant Information', 'Experience', 'Review'];

const getFieldError = (errors: unknown, path: string): string | undefined => {
  const segments = path.split('.');
  let cursor = errors as Record<string, unknown> | undefined;

  for (const segment of segments) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = cursor[segment] as Record<string, unknown> | undefined;
  }

  const message = (cursor as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
};

const stripSelectedFiles = (values: GmfDeveloperPipelineFormValues, additionalDocs: GmfProjectFile[]): GmfDeveloperPipelineFormValues => {
  const { selectedFiles: _selectedFiles, ...projectDetails } = values.projectDetails;

  return {
    ...values,
    projectDetails: {
      ...projectDetails,
      city: 'Milwaukee',
      state: 'Wisconsin',
      dealType: 'Homeownership',
      additionalDocs,
    },
  };
};

export const GMFDeveloperPipelineApplicationForm = ({ formData }: GMFDeveloperPipelineApplicationFormProps) => {
  const params = useParams<string>();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => new Set());
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const { upload } = useFileUpload({ signAsset: signApplicationPublicAsset });

  const formMethods = useForm<GmfDeveloperPipelineFormValues>({
    defaultValues: initialGmfDeveloperPipelineValues,
    mode: 'all',
    resolver: zodResolver(gmfStepSchemas[currentStep]) as unknown as Resolver<GmfDeveloperPipelineFormValues>,
  });

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    setValue,
    trigger,
  } = formMethods;

  const selectedFiles = formMethods.watch('projectDetails.selectedFiles') ?? [];

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => setIsSubmitSuccess(true),
  });

  const uploadFiles = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload));

  const isLastStep = currentStep === stepTitles.length - 1;

  const canGoToStep = (step: number) => step <= currentStep || completedSteps.has(step) || completedSteps.has(step - 1);

  const goToStep = (step: number) => {
    if (canGoToStep(step)) {
      setCurrentStep(step);
    }
  };

  const nextStep = async () => {
    const isStepValid = await trigger();
    if (!isStepValid) return;

    setCompletedSteps((previous) => new Set(previous).add(currentStep));
    setCurrentStep((previous) => Math.min(previous + 1, stepTitles.length - 1));
  };

  const previousStep = () => setCurrentStep((previous) => Math.max(previous - 1, 0));

  const onSubmit = async (values: GmfDeveloperPipelineFormValues) => {
    setUploadError('');
    const parsedValues = gmfDeveloperPipelineSchema.parse(values);
    let uploadedDocs = parsedValues.projectDetails.additionalDocs;

    const files = parsedValues.projectDetails.selectedFiles ?? [];

    if (files.length > 0) {
      try {
        const signAssetPayload = files.map((file) => ({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          formId: String(params.applicationFormId ?? formData.data.id),
          field: 'additionalDocs',
        }));
        const { sign, files: uploadedFiles }: UploadFnReturn = await uploadFiles.mutateAsync({ signAssetPayload, files });
        const signResponses: SignedApplicationAssetResponse[] = Array.isArray(sign) ? sign : [sign];
        uploadedDocs = signResponses.map((signature, index) => ({
          name: uploadedFiles[index].name,
          path: signature.path,
          key: signature.key,
          type: uploadedFiles[index].type,
          field: 'additionalDocs',
          description: 'Additional Documents',
        }));
      } catch (_error) {
        setUploadError('Failed to upload additional documents.');
        return;
      }
    }

    const applicationData = stripSelectedFiles(parsedValues, uploadedDocs);

    submitApplication.mutate({
      formId: formData.data.id,
      contactName: applicationData.applicantInformation.contactPersonName,
      contactEmail: applicationData.applicantInformation.contactPersonEmail,
      applicationData,
      files: uploadedDocs,
    });
  };

  const confirmationMessage =
    formData.data.confirmationMessage ?? 'Thank you for submitting your project to the Greater Milwaukee Foundation Developer Pipeline.';

  if (isSubmitSuccess) {
    return <SubmitSuccess message={confirmationMessage} />;
  }

  return (
    <div className='min-h-screen bg-slate-50 px-4 py-8'>
      <div className='mx-auto flex max-w-7xl flex-col gap-8 lg:flex-row'>
        <aside className='lg:w-72 lg:shrink-0'>
          <div className='rounded-md border border-slate-200 bg-white p-5 lg:sticky lg:top-8'>
            <div className='mb-6'>
              {formData.data.logo && <img src={formData.data.logo} alt='Form logo' className='mb-4 h-14 object-contain' />}
              <p className='text-sm text-slate-500'>Greater Milwaukee Foundation</p>
              <h1 className='text-xl font-semibold text-slate-950'>{formData.data.name}</h1>
            </div>
            <div className='hidden lg:block'>
              <StepNavigation steps={stepTitles} currentStep={currentStep} completedSteps={completedSteps} onStepClick={goToStep} />
            </div>
            <div className='flex gap-2 overflow-x-auto pb-1 lg:hidden'>
              {stepTitles.map((title, index) => (
                <button
                  key={title}
                  type='button'
                  disabled={!canGoToStep(index)}
                  onClick={() => goToStep(index)}
                  className={cn(
                    'h-10 shrink-0 cursor-pointer rounded-md px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                    currentStep === index ? 'bg-primary text-primary-foreground' : 'bg-white text-slate-700'
                  )}
                >
                  {index + 1}. {title}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className='min-w-0 flex-1'>
          <Card className='rounded-md py-0'>
            <CardContent className='p-5 md:p-8'>
              <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} className='space-y-8'>
                  {currentStep === 0 && <ProjectDetailsStep errors={errors} selectedFiles={selectedFiles} setValue={setValue} />}
                  {currentStep === 1 && <ApplicantInformationStep errors={errors} />}
                  {currentStep === 2 && <ExperienceStep errors={errors} control={control} />}
                  {currentStep === 3 && <ReviewStep values={getValues()} onEdit={setCurrentStep} />}

                  {uploadError && <p className='rounded-md bg-destructive/10 p-3 text-sm text-destructive'>{uploadError}</p>}
                  {submitApplication.isError && (
                    <p className='rounded-md bg-destructive/10 p-3 text-sm text-destructive'>Application submission failed.</p>
                  )}

                  <div className='flex items-center justify-between border-t border-slate-200 pt-6'>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={currentStep === 0 || submitApplication.isLoading}
                      onClick={previousStep}
                    >
                      <ChevronLeft className='size-4' />
                      Back
                    </Button>
                    {isLastStep ? (
                      <Button type='submit' disabled={submitApplication.isLoading || uploadFiles.isLoading}>
                        {submitApplication.isLoading || uploadFiles.isLoading ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    ) : (
                      <Button type='button' onClick={nextStep}>
                        Next
                        <ChevronRight className='size-4' />
                      </Button>
                    )}
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

interface StepProps {
  errors: unknown;
}

const TextField = ({
  label,
  name,
  errors,
  type = 'text',
  readOnly = false,
  placeholder,
}: {
  label: string;
  name: Path<GmfDeveloperPipelineFormValues>;
  errors: unknown;
  type?: string;
  readOnly?: boolean;
  placeholder?: string;
}) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();
  const fieldError = getFieldError(errors, name);
  return (
    <div className='space-y-2'>
      <Label htmlFor={name}>{label}</Label>
      <Input
        id={name}
        type={type}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-invalid={!!fieldError}
        className={cn(readOnly && 'bg-slate-100 text-slate-700')}
        {...methods.register(name)}
      />
      <FieldError message={fieldError} />
    </div>
  );
};

const TextAreaField = ({
  label,
  name,
  errors,
  placeholder,
}: {
  label: string;
  name: Path<GmfDeveloperPipelineFormValues>;
  errors: unknown;
  placeholder?: string;
}) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();
  const fieldError = getFieldError(errors, name);
  return (
    <div className='space-y-2'>
      <Label htmlFor={name}>{label}</Label>
      <Textarea id={name} rows={4} placeholder={placeholder} aria-invalid={!!fieldError} {...methods.register(name)} />
      <FieldError message={fieldError} />
    </div>
  );
};

const SelectField = ({
  label,
  name,
  options,
  errors,
}: {
  label: string;
  name: Path<GmfDeveloperPipelineFormValues>;
  options: string[];
  errors: unknown;
}) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();
  const fieldError = getFieldError(errors, name);

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field }) => (
        <div className='space-y-2'>
          <Label>{label}</Label>
          <Select value={String(field.value ?? '')} onValueChange={field.onChange}>
            <SelectTrigger className='w-full' aria-invalid={!!fieldError}>
              <SelectValue placeholder='Select an option' />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={fieldError} />
        </div>
      )}
    />
  );
};

const RadioField = ({
  label,
  name,
  options,
  errors,
}: {
  label: string;
  name: Path<GmfDeveloperPipelineFormValues>;
  options: readonly string[];
  errors: unknown;
}) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();
  const fieldError = getFieldError(errors, name);

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field }) => (
        <div className='space-y-3'>
          <Label>{label}</Label>
          <RadioGroup className='flex flex-wrap gap-5' value={String(field.value ?? '')} onValueChange={field.onChange}>
            {options.map((option) => (
              <div key={option} className='flex items-center gap-2'>
                <RadioGroupItem value={option} id={`${name}-${option}`} />
                <Label htmlFor={`${name}-${option}`} className='cursor-pointer font-normal'>
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <FieldError message={fieldError} />
        </div>
      )}
    />
  );
};

const CheckboxGroupField = ({
  label,
  name,
  options,
}: {
  label: string;
  name: 'applicantInformation.applicantIdentity' | 'applicantInformation.professionalTrainingHistory';
  options: string[];
}) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field }) => {
        const value = Array.isArray(field.value) ? (field.value as string[]) : [];
        return (
          <div className='space-y-3'>
            <Label>{label}</Label>
            <div className='grid gap-2 sm:grid-cols-2'>
              {options.map((option) => (
                <label key={option} className='flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 p-3 text-sm'>
                  <Checkbox
                    checked={value.includes(option)}
                    onCheckedChange={(checked) => {
                      const nextValue = checked ? [...value, option] : value.filter((item) => item !== option);
                      field.onChange(nextValue);
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      }}
    />
  );
};

const ProjectDetailsStep = ({
  errors,
  selectedFiles,
  setValue,
}: StepProps & {
  selectedFiles: File[];
  setValue: ReturnType<typeof useForm<GmfDeveloperPipelineFormValues>>['setValue'];
}) => {
  const additionalDocsInputId = useId();

  return (
    <FormStepShell title='Project Details' subtitle='Tell us about the homeownership development project you are proposing in Milwaukee.'>
      <div className='grid gap-4 md:grid-cols-3'>
        <TextField label='City' name='projectDetails.city' errors={errors} readOnly />
        <TextField label='State' name='projectDetails.state' errors={errors} readOnly />
        <TextField label='Deal Type' name='projectDetails.dealType' errors={errors} readOnly />
      </div>
      <TextField label='Street Address' name='projectDetails.streetAddress' errors={errors} placeholder='123 Main Street' />
      <div className='grid gap-4 md:grid-cols-2'>
        <TextField label='ZIP / Postal Code' name='projectDetails.zipCode' errors={errors} placeholder='53206' />
        <TextField label='Proposed Number of Units' name='projectDetails.proposedUnits' errors={errors} type='number' placeholder='0' />
      </div>
      <div className='grid gap-4 md:grid-cols-2'>
        <RadioField
          label='Is Proposed Project By-Right?'
          name='projectDetails.projectByRight'
          options={projectByRightOptions}
          errors={errors}
        />
        <SelectField
          label='Status of Site Control'
          name='projectDetails.siteControlStatus'
          options={siteControlStatusOptions}
          errors={errors}
        />
      </div>
      <div className='grid gap-4 md:grid-cols-2'>
        <SelectField label='Construction Type' name='projectDetails.constructionType' options={constructionTypeOptions} errors={errors} />
        <SelectField label='Acquisition Type' name='projectDetails.acquisitionType' options={acquisitionTypeOptions} errors={errors} />
      </div>
      <div className='grid gap-4 md:grid-cols-3'>
        <TextField label='Estimated Acquisition Cost' name='projectDetails.acquisitionCostEstimate' errors={errors} placeholder='$0' />
        <TextField label='Estimated Development Cost' name='projectDetails.developmentCostEstimate' errors={errors} placeholder='$0' />
        <TextField
          label='Estimated Predevelopment Cost'
          name='projectDetails.preDevelopmentCostEstimate'
          errors={errors}
          placeholder='$0'
        />
      </div>
      <TextAreaField
        label='Community Need'
        name='projectDetails.communityNeed'
        errors={errors}
        placeholder='Explain why this project is needed in this location for this population.'
      />
      <TextAreaField label='Target Population' name='projectDetails.targetPopulation' errors={errors} />
      <TextAreaField
        label='Preliminary Financing Strategy'
        name='projectDetails.preliminaryFinancingStrategy'
        errors={errors}
        placeholder='Include anticipated funding sources.'
      />
      <TextAreaField
        label='Preliminary Development Timeline'
        name='projectDetails.preliminaryDevelopmentTimeline'
        errors={errors}
        placeholder='Include key milestones.'
      />
      <TextAreaField
        label='Key Partners'
        name='projectDetails.keyPartners'
        errors={errors}
        placeholder='Architect, contractor, lender, city agency, community organization, or other partners.'
      />
      <div className='space-y-2'>
        <Label htmlFor={additionalDocsInputId}>Additional Documents</Label>
        <label
          htmlFor={additionalDocsInputId}
          className='flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-300 p-8 text-center hover:border-primary'
        >
          <Upload className='mb-2 size-8 text-slate-500' />
          <span className='text-sm text-slate-600'>Choose files to upload</span>
          <span className='text-xs text-slate-500'>PDF, DOC, or images</span>
        </label>
        <Input
          id={additionalDocsInputId}
          type='file'
          multiple
          className='sr-only'
          onChange={(event) => setValue('projectDetails.selectedFiles', Array.from(event.target.files ?? []), { shouldDirty: true })}
        />
        {selectedFiles.length > 0 && <p className='text-sm text-slate-600'>{selectedFiles.map((file) => file.name).join(', ')}</p>}
      </div>
    </FormStepShell>
  );
};

const ApplicantInformationStep = ({ errors }: StepProps) => {
  const methods = useFormContext<GmfDeveloperPipelineFormValues>();
  const residentialAddressDifferent = methods.watch('applicantInformation.residentialAddressDifferent');

  return (
    <FormStepShell title='Applicant Information' subtitle='Tell us about your organization and primary contact.'>
      <div className='grid gap-4 md:grid-cols-2'>
        <TextField label='Entity Name' name='applicantInformation.entityName' errors={errors} />
        <TextField label='Business Entity State of Organization' name='applicantInformation.businessEntityState' errors={errors} />
      </div>
      <div className='grid gap-4 md:grid-cols-2'>
        <TextField label='Website, if available' name='applicantInformation.website' errors={errors} placeholder='https://example.org' />
        <TextField label='Formation Date' name='applicantInformation.formationDate' errors={errors} type='date' />
      </div>
      <TextField label='Business Street Address' name='applicantInformation.businessStreetAddress' errors={errors} />
      <div className='grid gap-4 md:grid-cols-3'>
        <TextField label='Business City' name='applicantInformation.businessCity' errors={errors} />
        <TextField label='Business State / Province' name='applicantInformation.businessState' errors={errors} />
        <TextField label='Business ZIP / Postal Code' name='applicantInformation.businessZipCode' errors={errors} />
      </div>
      <TextField label='Contact Person Full Name' name='applicantInformation.contactPersonName' errors={errors} />
      <div className='grid gap-4 md:grid-cols-2'>
        <TextField label='Contact Person Email' name='applicantInformation.contactPersonEmail' errors={errors} type='email' />
        <TextField label='Contact Person Phone Number' name='applicantInformation.phone' errors={errors} type='tel' />
      </div>
      <div className='grid gap-4 md:grid-cols-2'>
        <TextField label='Date of Birth' name='applicantInformation.dateOfBirth' errors={errors} type='date' />
        <RadioField
          label='Residential address is different from business address?'
          name='applicantInformation.residentialAddressDifferent'
          options={yesNoOptions}
          errors={errors}
        />
      </div>
      {residentialAddressDifferent === 'Yes' && (
        <>
          <TextField label='Residential Street Address' name='applicantInformation.residentialStreetAddress' errors={errors} />
          <div className='grid gap-4 md:grid-cols-3'>
            <TextField label='Residential City' name='applicantInformation.residentialCity' errors={errors} />
            <TextField label='Residential State / Province' name='applicantInformation.residentialState' errors={errors} />
            <TextField label='Residential ZIP / Postal Code' name='applicantInformation.residentialZipCode' errors={errors} />
          </div>
        </>
      )}
      <div className='grid gap-4 md:grid-cols-2'>
        <RadioField label='Are you an ACRE graduate?' name='applicantInformation.acreGraduate' options={yesNoOptions} errors={errors} />
        <SelectField
          label='Organization Type'
          name='applicantInformation.organizationType'
          options={organizationTypeOptions}
          errors={errors}
        />
      </div>
      <CheckboxGroupField
        label='Applicant identity checkboxes'
        name='applicantInformation.applicantIdentity'
        options={applicantIdentityOptions}
      />
      <TextAreaField
        label='Educational Background'
        name='applicantInformation.educationBackground'
        errors={errors}
        placeholder='Include institutions attended, degrees earned, and dates.'
      />
      <TextAreaField
        label='Professional Licenses or Certifications'
        name='applicantInformation.professionalLicenses'
        errors={errors}
        placeholder='Contractor, broker, architect, or other active licenses.'
      />
      <CheckboxGroupField
        label='Professional Training History'
        name='applicantInformation.professionalTrainingHistory'
        options={professionalTrainingOptions}
      />
      <TextAreaField
        label='Real Estate Entities Owned or Managed'
        name='applicantInformation.realEstateEntities'
        errors={errors}
        placeholder='List all real estate entities in which you hold or have held ownership or management interest.'
      />
      <TextAreaField
        label='Current Employer and Role'
        name='applicantInformation.currentEmployerRole'
        errors={errors}
        placeholder='Complete this if development work is not full-time.'
      />
    </FormStepShell>
  );
};

const emptyProject = (): GmfCompletedProject => ({
  projectName: '',
  address: '',
  units: '',
  role: '',
  completionYear: '',
});

const ExperienceStep = ({
  errors,
  control,
}: StepProps & {
  control: ReturnType<typeof useForm<GmfDeveloperPipelineFormValues>>['control'];
}) => (
  <FormStepShell title='Development Experience' subtitle='Tell us about projects completed by your company or key principals.'>
    <ProjectArrayField
      title='Company Completed Projects'
      description='Please provide up to three projects completed by the applicant company. If the company has not completed three projects, provide the projects that are available and complete the Principal Experience section below.'
      emptyState='No company completed projects have been added.'
      buttonLabel='Add Company Project'
      fieldName='experience.companyCompleted'
      errors={errors}
      control={control}
    />
    <ProjectArrayField
      title='Principal Completed Projects'
      description='If the applicant company has not completed three projects, provide projects completed by a key principal.'
      emptyState='No principal completed projects have been added.'
      buttonLabel='Add Principal Project'
      fieldName='experience.principalCompleted'
      errors={errors}
      control={control}
    />
  </FormStepShell>
);

const ProjectArrayField = ({
  title,
  description,
  emptyState,
  buttonLabel,
  fieldName,
  errors,
  control,
}: {
  title: string;
  description: string;
  emptyState: string;
  buttonLabel: string;
  fieldName: 'experience.companyCompleted' | 'experience.principalCompleted';
  errors: unknown;
  control: ReturnType<typeof useForm<GmfDeveloperPipelineFormValues>>['control'];
}) => {
  const { fields, append, remove } = useFieldArray({ control, name: fieldName });

  return (
    <div className='rounded-md border border-slate-200 p-5'>
      <div className='mb-5'>
        <h3 className='text-lg font-medium text-slate-950'>{title}</h3>
        <p className='mt-1 text-sm text-slate-600'>{description}</p>
      </div>
      {fields.length === 0 ? (
        <p className='rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500'>{emptyState}</p>
      ) : (
        <div className='space-y-5'>
          {fields.map((field, index) => (
            <div key={field.id} className='rounded-md bg-slate-50 p-4'>
              <div className='mb-4 flex items-center justify-between gap-4'>
                <h4 className='font-medium text-slate-800'>Project {index + 1}</h4>
                <Button type='button' variant='ghost' size='sm' onClick={() => remove(index)}>
                  <Trash2 className='size-4' />
                  Remove
                </Button>
              </div>
              <div className='grid gap-4 md:grid-cols-2'>
                <TextField label='Project Name' name={`${fieldName}.${index}.projectName`} errors={errors} />
                <TextField label='Project Address' name={`${fieldName}.${index}.address`} errors={errors} />
                <TextField label='Units' name={`${fieldName}.${index}.units`} errors={errors} type='number' />
                <TextField label='Your Role' name={`${fieldName}.${index}.role`} errors={errors} />
                <TextField label='Completion Year' name={`${fieldName}.${index}.completionYear`} errors={errors} />
              </div>
            </div>
          ))}
        </div>
      )}
      <Button type='button' variant='outline' className='mt-5' disabled={fields.length >= 3} onClick={() => append(emptyProject())}>
        <Plus className='size-4' />
        {buttonLabel}
      </Button>
    </div>
  );
};

const ReviewStep = ({ values, onEdit }: { values: GmfDeveloperPipelineFormValues; onEdit: (step: number) => void }) => {
  const projectSummary = useMemo(
    () => [
      ['Location', `${values.projectDetails.streetAddress}, Milwaukee, Wisconsin ${values.projectDetails.zipCode}`],
      ['Deal Type', values.projectDetails.dealType],
      ['Proposed Units', values.projectDetails.proposedUnits],
      ['Development Cost', values.projectDetails.developmentCostEstimate],
      ['Site Control', values.projectDetails.siteControlStatus],
    ],
    [values.projectDetails]
  );

  return (
    <FormStepShell title='Review & Submit' subtitle='Please review your information before submitting.'>
      <ReviewCard title='Project Details' onEdit={() => onEdit(0)} rows={projectSummary} />
      <ReviewCard
        title='Applicant Information'
        onEdit={() => onEdit(1)}
        rows={[
          ['Entity Name', values.applicantInformation.entityName],
          ['Contact', `${values.applicantInformation.contactPersonName} (${values.applicantInformation.contactPersonEmail})`],
          ['ACRE Graduate', values.applicantInformation.acreGraduate],
          ['Organization Type', values.applicantInformation.organizationType],
        ]}
      />
      <ReviewCard
        title='Experience'
        onEdit={() => onEdit(2)}
        rows={[
          ['Company Projects', `${values.experience.companyCompleted.length}`],
          ['Principal Projects', `${values.experience.principalCompleted.length}`],
        ]}
      />
    </FormStepShell>
  );
};

const ReviewCard = ({ title, rows, onEdit }: { title: string; rows: string[][]; onEdit: () => void }) => (
  <div className='rounded-md border border-slate-200'>
    <div className='flex items-center justify-between border-b border-slate-200 px-4 py-3'>
      <h3 className='font-medium text-slate-950'>{title}</h3>
      <Button type='button' variant='ghost' size='sm' onClick={onEdit}>
        Edit
      </Button>
    </div>
    <dl className='divide-y divide-slate-100'>
      {rows.map(([label, value]) => (
        <div key={label} className='grid gap-2 px-4 py-3 text-sm sm:grid-cols-3'>
          <dt className='font-medium text-slate-600'>{label}</dt>
          <dd className='sm:col-span-2'>{value || 'Not provided'}</dd>
        </div>
      ))}
    </dl>
  </div>
);
